import React from 'react';

import { getSingleBorder } from '@coachhubio/nova-borders';
import { Skeleton } from '@coachhubio/nova-skeleton';
import { gap, paddingY } from '@coachhubio/nova-spaces';
import { Text } from '@coachhubio/nova-text';
import { useTranslation } from 'react-i18next';
import { Row, InlinePlaceholder } from 'screens/Dashboard/components/Common';
import { DynamicIcon } from 'screens/Dashboard/components/DynamicIcon';
import styled from 'styled-components';

import { ImpactFocusAreaProgressGoalAchievement } from './TopFocusAreas';
import GoalPercentageGraph from '../../../components/GoalPercentageGraph';

const Container = styled.div`
  ${gap('s')}
  ${paddingY('m')}
  ${getSingleBorder({ color: 'neutral80', position: 'top', width: 's' })}

  display: grid;
`;

const Card = styled.div`
  grid-template-columns: 1fr 1fr;
  display: grid;
`;

const LeftSideRow = styled(Row)`
  ${gap('m')}

  align-items: center;
`;

const RightSideRow = styled(Row)`
  justify-content: flex-end;
`;

type Props = {
  isLoading: boolean;
  area: ImpactFocusAreaProgressGoalAchievement;
};

const FocusAreaRow = ({ isLoading, area }: Props) => {
  const { t } = useTranslation('Dashboard');

  const percentage = ((area?.goal_attainment || 0) * 100).toFixed();

  return (
    <Container>
      {isLoading ? (
        <InlinePlaceholder $height="44px" $width="100%">
          <Skeleton height="44px" width="100%" />
        </InlinePlaceholder>
      ) : area?.['focus_areas.icon'] ? (
        <Card>
          <LeftSideRow>
            <DynamicIcon
              src={area['focus_areas.icon']}
              alt={`${area['focus_areas.title']} Focus Area Icon`}
              width={40}
              height={40}
            />
            <Text color="primaryDark35" modifier="bold">
              {t(area['focus_areas.title'].replace(/::/g, '.'))}
            </Text>
          </LeftSideRow>
          <RightSideRow>
            <GoalPercentageGraph progressPercentage={+percentage} arcWidth={4} svgWidth={32} />
          </RightSideRow>
        </Card>
      ) : (
        <Text color="primaryDark35" size="m">
          {t('common.no_data')}
        </Text>
      )}
    </Container>
  );
};

export default FocusAreaRow;
